import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Globe, Sparkles, Building, Mail, Phone, MapPin, 
  ArrowLeft, Loader2, ArrowRight, Save, Edit, Plus, 
  Trash2, Eye, Palette, Check, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getAuthHeaders, getUser } from '../lib/auth';
import { websiteTemplates, getTemplateById } from '../data/websiteTemplates';
import { getTemplateForCategory, getAllCategoryTemplates } from '../data/categoryWebsiteTemplates';
import CustomDomainManager from '../components/CustomDomainManager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuickWebsiteBuilder = () => {
  const navigate = useNavigate();
  const currentUser = getUser();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [website, setWebsite] = useState(null);
  
  // Wizard steps: 'template' -> 'collect' -> 'preview' -> 'published'
  const [step, setStep] = useState('template');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Business information
  const [businessInfo, setBusinessInfo] = useState({
    services: [],
    about: '',
    portfolio: [],
    hours: '',
    tagline: '',
    pricing: [],
    testimonials: [],
    faq: [],
    team: [],
    newsletter: { enabled: false, description: '' }
  });
  
  // Template customization
  const [template, setTemplate] = useState({
    heroHeading: '',
    heroSubheading: '',
    aboutHeading: 'About Us',
    servicesHeading: 'Our Services',
    portfolioHeading: 'Our Work',
    contactHeading: 'Get In Touch',
    primaryColor: '#2563eb',
    accentColor: '#3b82f6'
  });
  
  // Editing state
  const [editingSection, setEditingSection] = useState(null);

  useEffect(() => {
    if (currentUser) {
      fetchUserProfile();
      checkExistingWebsite();
    } else {
      // Demo mode - show pre-built template
      loadDemoTemplate();
    }
  }, []);

  const loadDemoTemplate = () => {
    // Load a full demo template for non-logged-in users
    setUserProfile({
      name: 'Demo Business',
      businessName: 'TechVision Solutions',
      businessCategory: 'Technology Consultant',
      email: 'hello@techvision.com',
      phone: '+1-555-0100',
      address: '123 Innovation Drive, San Francisco, CA 94105'
    });

    setSelectedTemplate('modern-business');
    
    setTemplate({
      heroHeading: 'TechVision Solutions',
      heroSubheading: 'Transform Your Business with Cutting-Edge Technology',
      aboutHeading: 'About Us',
      servicesHeading: 'Our Services',
      portfolioHeading: 'Our Work',
      contactHeading: 'Get In Touch',
      primaryColor: '#2563eb',
      accentColor: '#3b82f6'
    });

    setBusinessInfo({
      services: [
        { name: 'Cloud Solutions', description: 'Scalable cloud infrastructure and migration services', icon: '☁️' },
        { name: 'AI Integration', description: 'Custom AI and machine learning solutions', icon: '🤖' },
        { name: 'Cybersecurity', description: 'Enterprise-grade security implementation', icon: '🔒' },
        { name: 'Data Analytics', description: 'Business intelligence and data insights', icon: '📊' }
      ],
      about: 'TechVision Solutions is a leading technology consultancy helping businesses transform digitally. With over 10 years of experience, we deliver innovative solutions that drive growth.',
      portfolio: [
        { title: 'E-Commerce Platform', description: 'Built scalable platform handling 1M+ transactions', image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800' },
        { title: 'AI Chatbot System', description: 'Reduced customer service costs by 60%', image: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=800' },
        { title: 'Cloud Migration', description: 'Migrated Fortune 500 company to cloud', image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800' }
      ],
      hours: 'Monday - Friday: 9am - 6pm',
      tagline: 'Your Partner in Digital Transformation',
      pricing: [
        { name: 'Starter', price: '$2,999', features: ['Cloud Setup', 'Basic Security', 'Email Support'] },
        { name: 'Business', price: '$9,999', features: ['Full Cloud Migration', 'Advanced Security', 'Priority Support', 'AI Integration'] },
        { name: 'Enterprise', price: 'Custom', features: ['Everything in Business', 'Dedicated Team', '24/7 Support', 'Custom Solutions'] }
      ],
      testimonials: [
        { name: 'Sarah Johnson', company: 'TechCorp Inc', text: 'TechVision transformed our business operations. Highly recommended!', image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200' },
        { name: 'Michael Chen', company: 'StartUp Co', text: 'Their AI solutions helped us scale 10x faster.', image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200' }
      ],
      faq: [
        { question: 'How long does implementation take?', answer: 'Most projects are completed within 4-8 weeks.' },
        { question: 'Do you offer ongoing support?', answer: 'Yes, we provide 24/7 support for all enterprise clients.' }
      ],
      team: [
        { name: 'Alex Morgan', role: 'CEO & Founder', image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=300' },
        { name: 'Sarah Chen', role: 'CTO', image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=300' }
      ],
      newsletter: { enabled: true, description: 'Get weekly tech insights and updates' }
    });

    setStep('preview');
    setLoading(false);
  };

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: getAuthHeaders()
      });
      setUserProfile(response.data);
      
      // Get category-specific template based on user's business category
      const suggestedTemplate = getTemplateForCategory(response.data.businessCategory);
      
      // Pre-fill template with business card data and category colors
      setTemplate(prev => ({
        ...prev,
        heroHeading: response.data.businessName || '',
        heroSubheading: response.data.businessCategory || 'Professional Services',
        primaryColor: suggestedTemplate.colors.primary,
        accentColor: suggestedTemplate.colors.accent
      }));
      
      setBusinessInfo(prev => ({
        ...prev,
        tagline: response.data.bio || ''
      }));
      
      // Auto-select the suggested template
      setSelectedTemplate(suggestedTemplate);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load your profile');
    } finally {
      setLoading(false);
    }
  };

  const checkExistingWebsite = async () => {
    try {
      const response = await axios.get(`${API}/auto-websites/my-website`, {
        headers: getAuthHeaders()
      });
      if (response.data.website) {
        setWebsite(response.data.website);
        if (response.data.website.optionalInfo) {
          // Ensure all new fields have default values
          setBusinessInfo({
            services: response.data.website.optionalInfo.services || [],
            about: response.data.website.optionalInfo.about || '',
            portfolio: response.data.website.optionalInfo.portfolio || [],
            hours: response.data.website.optionalInfo.hours || '',
            tagline: response.data.website.optionalInfo.tagline || '',
            pricing: response.data.website.optionalInfo.pricing || [],
            testimonials: response.data.website.optionalInfo.testimonials || [],
            faq: response.data.website.optionalInfo.faq || [],
            team: response.data.website.optionalInfo.team || [],
            newsletter: response.data.website.optionalInfo.newsletter || { enabled: false, description: '' }
          });
        }
        if (response.data.website.templateData) {
          setTemplate(response.data.website.templateData);
        }
        if (response.data.website.selectedTemplate) {
          setSelectedTemplate(response.data.website.selectedTemplate);
        }
        // If they have a website, show published step
        setStep('published');
      }
    } catch (error) {
      // No existing website, user will start from template selection
      console.log('No existing website - starting fresh');
    }
  };

  const addService = () => {
    setBusinessInfo({
      ...businessInfo,
      services: [...businessInfo.services, { title: '', description: '', icon: '⭐' }]
    });
  };

  const updateService = (index, field, value) => {
    const updated = [...businessInfo.services];
    updated[index][field] = value;
    setBusinessInfo({ ...businessInfo, services: updated });
  };

  const removeService = (index) => {
    const updated = businessInfo.services.filter((_, i) => i !== index);
    setBusinessInfo({ ...businessInfo, services: updated });
  };

  const addPortfolioItem = () => {
    setBusinessInfo({
      ...businessInfo,
      portfolio: [...businessInfo.portfolio, { title: '', description: '', image: '' }]
    });
  };

  const updatePortfolioItem = (index, field, value) => {
    const updated = [...businessInfo.portfolio];
    updated[index][field] = value;
    setBusinessInfo({ ...businessInfo, portfolio: updated });
  };

  const removePortfolioItem = (index) => {
    const updated = businessInfo.portfolio.filter((_, i) => i !== index);
    setBusinessInfo({ ...businessInfo, portfolio: updated });
  };

  // Portfolio Image Upload
  const handlePortfolioImageUpload = async (index, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      toast.loading('Uploading image...');
      const response = await axios.post(`${API}/images/upload`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      updatePortfolioItem(index, 'image', imageUrl);
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  // Pricing Helpers
  const addPricingPlan = () => {
    setBusinessInfo({
      ...businessInfo,
      pricing: [...businessInfo.pricing, { name: '', price: '', features: [], popular: false }]
    });
  };

  const updatePricingPlan = (index, field, value) => {
    const updated = [...businessInfo.pricing];
    updated[index][field] = value;
    setBusinessInfo({ ...businessInfo, pricing: updated });
  };

  const removePricingPlan = (index) => {
    const updated = businessInfo.pricing.filter((_, i) => i !== index);
    setBusinessInfo({ ...businessInfo, pricing: updated });
  };

  // Testimonials Helpers
  const addTestimonial = () => {
    setBusinessInfo({
      ...businessInfo,
      testimonials: [...businessInfo.testimonials, { name: '', role: '', text: '', rating: 5 }]
    });
  };

  const updateTestimonial = (index, field, value) => {
    const updated = [...businessInfo.testimonials];
    updated[index][field] = value;
    setBusinessInfo({ ...businessInfo, testimonials: updated });
  };

  const removeTestimonial = (index) => {
    const updated = businessInfo.testimonials.filter((_, i) => i !== index);
    setBusinessInfo({ ...businessInfo, testimonials: updated });
  };

  // FAQ Helpers
  const addFAQ = () => {
    setBusinessInfo({
      ...businessInfo,
      faq: [...businessInfo.faq, { question: '', answer: '' }]
    });
  };

  const updateFAQ = (index, field, value) => {
    const updated = [...businessInfo.faq];
    updated[index][field] = value;
    setBusinessInfo({ ...businessInfo, faq: updated });
  };

  const removeFAQ = (index) => {
    const updated = businessInfo.faq.filter((_, i) => i !== index);
    setBusinessInfo({ ...businessInfo, faq: updated });
  };

  // Team Helpers
  const addTeamMember = () => {
    setBusinessInfo({
      ...businessInfo,
      team: [...businessInfo.team, { name: '', role: '', bio: '', photo: '' }]
    });
  };

  const updateTeamMember = (index, field, value) => {
    const updated = [...businessInfo.team];
    updated[index][field] = value;
    setBusinessInfo({ ...businessInfo, team: updated });
  };

  const removeTeamMember = (index) => {
    const updated = businessInfo.team.filter((_, i) => i !== index);
    setBusinessInfo({ ...businessInfo, team: updated });
  };

  const handleTeamPhotoUpload = async (index, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      toast.loading('Uploading photo...');
      const response = await axios.post(`${API}/images/upload`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      updateTeamMember(index, 'photo', imageUrl);
      toast.success('Photo uploaded!');
    } catch (error) {
      toast.error('Failed to upload photo');
    }
  };

  const goToPreview = () => {
    if (!userProfile?.businessName) {
      toast.error('Please complete your business card first');
      navigate('/profile/edit');
      return;
    }
    setStep('preview');
    toast.success('Click any section to edit it!');
  };

  const publishWebsite = async () => {
    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/auto-websites/generate`,
        {
          optionalInfo: businessInfo,
          templateData: template,
          selectedTemplate: selectedTemplate
        },
        { headers: getAuthHeaders() }
      );

      setWebsite(response.data.website);
      setStep('published');
      
      // Show appropriate success message
      if (website) {
        toast.success('✅ Website Updated & Republished!');
      } else {
        toast.success('🎉 Website Published Successfully!');
      }
    } catch (error) {
      console.error('Error publishing:', error);
      toast.error('Failed to save website');
    } finally {
      setSaving(false);
    }
  };

  const updateAndRepublish = async () => {
    await publishWebsite();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // STEP 0: CHOOSE TEMPLATE
  if (step === 'template') {
    // Get the suggested template for user's category
    const suggestedTemplate = selectedTemplate || getTemplateForCategory(userProfile?.businessCategory);
    const allTemplates = getAllCategoryTemplates();
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Template</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We've selected a template designed specifically for {userProfile?.businessCategory || 'your business'}
            </p>
          </div>

          {/* Recommended Template - Featured */}
          {suggestedTemplate && (
            <div className="mb-12">
              <div className="flex items-center justify-center gap-2 mb-6">
                <Sparkles className="w-5 h-5 text-yellow-500" />
                <h2 className="text-2xl font-bold text-gray-900">Recommended For You</h2>
                <Sparkles className="w-5 h-5 text-yellow-500" />
              </div>
              
              <div className="max-w-2xl mx-auto">
                <div
                  onClick={() => {
                    setSelectedTemplate(suggestedTemplate);
                    setTemplate({
                      heroHeading: userProfile?.businessName || '',
                      heroSubheading: userProfile?.businessCategory || 'Professional Services',
                      aboutHeading: 'About Us',
                      servicesHeading: 'Our Services',
                      portfolioHeading: 'Our Work',
                      contactHeading: 'Get In Touch',
                      primaryColor: suggestedTemplate.colors.primary,
                      accentColor: suggestedTemplate.colors.accent
                    });
                    setStep('collect');
                    toast.success(`${suggestedTemplate.name} template selected!`);
                  }}
                  className="group cursor-pointer"
                >
                  <Card className="overflow-hidden hover:shadow-2xl transition-all border-4 border-yellow-400 relative">
                    {/* Recommended Badge */}
                    <div className="absolute top-4 left-4 z-10">
                      <div className="bg-yellow-400 text-gray-900 px-4 py-2 rounded-full font-bold shadow-lg flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        Perfect for {userProfile?.businessCategory}
                      </div>
                    </div>
                    
                    <div className="relative h-80 overflow-hidden">
                      <img
                        src={suggestedTemplate.thumbnail}
                        alt={suggestedTemplate.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                        <h3 className="text-3xl font-bold mb-3">{suggestedTemplate.name}</h3>
                        <p className="text-lg text-gray-200 mb-4">{suggestedTemplate.description || 'Tailored specifically for your industry'}</p>
                        <div className="bg-white text-gray-900 px-6 py-3 rounded-full font-bold inline-flex items-center gap-2 group-hover:scale-105 transition-transform">
                          Use This Template →
                        </div>
                      </div>
                    </div>
                    
                    {/* Color Palette Preview */}
                    <div className="p-6 bg-white">
                      <div className="flex items-center justify-center gap-3">
                        <span className="text-sm text-gray-600 font-medium">Industry Colors:</span>
                        <div className="flex gap-3">
                          <div 
                            className="w-8 h-8 rounded-full border-2 border-gray-200 shadow" 
                            style={{ backgroundColor: suggestedTemplate.colors.primary }}
                          ></div>
                          <div 
                            className="w-8 h-8 rounded-full border-2 border-gray-200 shadow" 
                            style={{ backgroundColor: suggestedTemplate.colors.secondary }}
                          ></div>
                          <div 
                            className="w-8 h-8 rounded-full border-2 border-gray-200 shadow" 
                            style={{ backgroundColor: suggestedTemplate.colors.accent }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          )}

          {/* Other Templates */}
          <div className="text-center mb-6">
            <h3 className="text-xl font-semibold text-gray-700">Or Browse Other Templates</h3>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {allTemplates.filter(t => t.id !== suggestedTemplate?.id).map((template) => (
              <div
                key={template.id}
                onClick={() => {
                  setSelectedTemplate(template);
                  setTemplate({
                    heroHeading: userProfile?.businessName || '',
                    heroSubheading: userProfile?.businessCategory || 'Professional Services',
                    aboutHeading: 'About Us',
                    servicesHeading: 'Our Services',
                    portfolioHeading: 'Our Work',
                    contactHeading: 'Get In Touch',
                    primaryColor: template.colors.primary,
                    accentColor: template.colors.accent
                  });
                  setStep('collect');
                  toast.success(`${template.name} template selected!`);
                }}
                className="group cursor-pointer"
              >
                <Card className="overflow-hidden hover:shadow-2xl transition-all border-2 hover:border-blue-500">
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={template.thumbnail}
                      alt={template.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      <h3 className="text-2xl font-bold mb-2">{template.name}</h3>
                      <p className="text-sm text-gray-200">{template.category}</p>
                    </div>
                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="bg-blue-600 text-white px-4 py-2 rounded-full font-medium shadow-lg">
                        Select Template →
                      </div>
                    </div>
                  </div>
                  
                  {/* Color Palette Preview */}
                  <div className="p-4 bg-white">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600 font-medium">Colors:</span>
                      <div className="flex gap-2">
                        <div 
                          className="w-6 h-6 rounded-full border-2 border-gray-200" 
                          style={{ backgroundColor: template.colors.primary }}
                        ></div>
                        <div 
                          className="w-6 h-6 rounded-full border-2 border-gray-200" 
                          style={{ backgroundColor: template.colors.secondary }}
                        ></div>
                        <div 
                          className="w-6 h-6 rounded-full border-2 border-gray-200" 
                          style={{ backgroundColor: template.colors.accent }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </div>

          <div className="text-center text-gray-600">
            <p className="text-sm">✨ All templates include professional stock images and are fully responsive</p>
          </div>
        </div>
      </div>
    );
  }

  // STEP 1: COLLECT INFORMATION
  if (step === 'collect') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Globe className="w-8 h-8 text-blue-600" />
              <h1 className="text-3xl font-bold text-gray-900">Quick Website Builder</h1>
            </div>
            <p className="text-gray-600">Step 1: Add your business information</p>
          </div>

          {/* Progress */}
          <div className="mb-8">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-blue-600 rounded-full"></div>
              <div className="flex-1 h-2 bg-blue-600 rounded-full"></div>
              <div className="flex-1 h-2 bg-gray-200 rounded-full"></div>
              <div className="flex-1 h-2 bg-gray-200 rounded-full"></div>
            </div>
            <div className="flex justify-between text-xs text-gray-600 mt-2">
              <span>1. Template</span>
              <span className="font-medium text-blue-600">2. Info</span>
              <span>3. Preview</span>
              <span>4. Publish</span>
            </div>
          </div>

          {/* Selected Template Badge */}
          {selectedTemplate && (
            <div className="mb-6 flex items-center justify-center gap-3">
              <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 px-4 py-2 rounded-full">
                <Sparkles className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">
                  {selectedTemplate.name} Template Selected
                </span>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setStep('template')}
              >
                Change Template
              </Button>
            </div>
          )}

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Your Business Card Info (Auto-filled)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <h3 className="font-bold text-lg">{userProfile?.businessName || 'Not set'}</h3>
                <p className="text-blue-600">{userProfile?.businessCategory || 'Not set'}</p>
                <div className="mt-3 space-y-1 text-sm">
                  {userProfile?.email && <p>📧 {userProfile.email}</p>}
                  {userProfile?.phone && <p>📱 {userProfile.phone}</p>}
                  {userProfile?.address && <p>📍 {userProfile.address}</p>}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Additional Information (Optional)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Tagline */}
              <div>
                <label className="block text-sm font-medium mb-2">Tagline/Slogan</label>
                <Input
                  value={businessInfo.tagline}
                  onChange={(e) => setBusinessInfo({ ...businessInfo, tagline: e.target.value })}
                  placeholder="e.g., Quality service you can trust"
                />
              </div>

              {/* About */}
              <div>
                <label className="block text-sm font-medium mb-2">About Your Business</label>
                <Textarea
                  value={businessInfo.about}
                  onChange={(e) => setBusinessInfo({ ...businessInfo, about: e.target.value })}
                  placeholder="Tell your story..."
                  rows={4}
                />
              </div>

              {/* Services */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium">Services/Products</label>
                  <Button onClick={addService} size="sm" variant="outline">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Service
                  </Button>
                </div>
                <div className="space-y-3">
                  {businessInfo.services.map((service, index) => (
                    <div key={index} className="border p-3 rounded-lg">
                      <div className="flex gap-2 mb-2">
                        <Input
                          placeholder="Service name"
                          value={service.title}
                          onChange={(e) => updateService(index, 'title', e.target.value)}
                          className="flex-1"
                        />
                        <Button onClick={() => removeService(index)} variant="ghost" size="sm">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <Textarea
                        placeholder="Description"
                        value={service.description}
                        onChange={(e) => updateService(index, 'description', e.target.value)}
                        rows={2}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Portfolio */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium">Portfolio/Work Samples</label>
                  <Button onClick={addPortfolioItem} size="sm" variant="outline">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Project
                  </Button>
                </div>
                <div className="space-y-3">
                  {businessInfo.portfolio.map((item, index) => (
                    <div key={index} className="border p-3 rounded-lg">
                      <div className="flex gap-2 mb-2">
                        <Input
                          placeholder="Project name"
                          value={item.title}
                          onChange={(e) => updatePortfolioItem(index, 'title', e.target.value)}
                          className="flex-1"
                        />
                        <Button onClick={() => removePortfolioItem(index)} variant="ghost" size="sm">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <div className="mb-2">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              handlePortfolioImageUpload(index, e.target.files[0]);
                            }
                          }}
                          className="text-sm"
                        />
                        <p className="text-xs text-gray-500 mt-1">Or paste URL below</p>
                      </div>
                      <Input
                        placeholder="Image URL"
                        value={item.image}
                        onChange={(e) => updatePortfolioItem(index, 'image', e.target.value)}
                        className="mb-2"
                      />
                      {item.image && (
                        <img src={item.image} alt="Preview" className="w-full h-32 object-cover rounded mb-2" />
                      )}
                      <Textarea
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updatePortfolioItem(index, 'description', e.target.value)}
                        rows={2}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Hours */}
              <div>
                <label className="block text-sm font-medium mb-2">Business Hours</label>
                <Textarea
                  value={businessInfo.hours}
                  onChange={(e) => setBusinessInfo({ ...businessInfo, hours: e.target.value })}
                  placeholder="Monday-Friday: 9AM-5PM"
                  rows={2}
                />
              </div>

              {/* Pricing */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium">Pricing Plans</label>
                  <Button onClick={addPricingPlan} size="sm" variant="outline">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Plan
                  </Button>
                </div>
                <div className="space-y-3">
                  {businessInfo.pricing.map((plan, index) => (
                    <div key={index} className="border p-3 rounded-lg">
                      <div className="flex gap-2 mb-2">
                        <Input
                          placeholder="Plan name (e.g., Basic)"
                          value={plan.name}
                          onChange={(e) => updatePricingPlan(index, 'name', e.target.value)}
                          className="flex-1"
                        />
                        <Input
                          placeholder="Price (e.g., $99/mo)"
                          value={plan.price}
                          onChange={(e) => updatePricingPlan(index, 'price', e.target.value)}
                          className="w-32"
                        />
                        <Button onClick={() => removePricingPlan(index)} variant="ghost" size="sm">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <Textarea
                        placeholder="Features (one per line)"
                        value={plan.features?.join('\n') || ''}
                        onChange={(e) => updatePricingPlan(index, 'features', e.target.value.split('\n'))}
                        rows={3}
                      />
                      <label className="flex items-center gap-2 mt-2">
                        <input
                          type="checkbox"
                          checked={plan.popular}
                          onChange={(e) => updatePricingPlan(index, 'popular', e.target.checked)}
                        />
                        <span className="text-sm">Mark as Popular</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Testimonials */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium">Testimonials</label>
                  <Button onClick={addTestimonial} size="sm" variant="outline">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Testimonial
                  </Button>
                </div>
                <div className="space-y-3">
                  {businessInfo.testimonials.map((testimonial, index) => (
                    <div key={index} className="border p-3 rounded-lg">
                      <div className="flex gap-2 mb-2">
                        <Input
                          placeholder="Customer name"
                          value={testimonial.name}
                          onChange={(e) => updateTestimonial(index, 'name', e.target.value)}
                          className="flex-1"
                        />
                        <Input
                          placeholder="Role/Company"
                          value={testimonial.role}
                          onChange={(e) => updateTestimonial(index, 'role', e.target.value)}
                          className="flex-1"
                        />
                        <Button onClick={() => removeTestimonial(index)} variant="ghost" size="sm">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <Textarea
                        placeholder="Testimonial text"
                        value={testimonial.text}
                        onChange={(e) => updateTestimonial(index, 'text', e.target.value)}
                        rows={3}
                      />
                      <div className="mt-2">
                        <label className="text-sm">Rating:</label>
                        <select
                          value={testimonial.rating}
                          onChange={(e) => updateTestimonial(index, 'rating', parseInt(e.target.value))}
                          className="ml-2 border rounded px-2 py-1"
                        >
                          {[5, 4, 3, 2, 1].map(r => (
                            <option key={r} value={r}>{'⭐'.repeat(r)}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* FAQ */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium">FAQ</label>
                  <Button onClick={addFAQ} size="sm" variant="outline">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Question
                  </Button>
                </div>
                <div className="space-y-3">
                  {businessInfo.faq.map((faq, index) => (
                    <div key={index} className="border p-3 rounded-lg">
                      <div className="flex gap-2 mb-2">
                        <Input
                          placeholder="Question"
                          value={faq.question}
                          onChange={(e) => updateFAQ(index, 'question', e.target.value)}
                          className="flex-1"
                        />
                        <Button onClick={() => removeFAQ(index)} variant="ghost" size="sm">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <Textarea
                        placeholder="Answer"
                        value={faq.answer}
                        onChange={(e) => updateFAQ(index, 'answer', e.target.value)}
                        rows={2}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Team */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium">Team Members</label>
                  <Button onClick={addTeamMember} size="sm" variant="outline">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Member
                  </Button>
                </div>
                <div className="space-y-3">
                  {businessInfo.team.map((member, index) => (
                    <div key={index} className="border p-3 rounded-lg">
                      <div className="flex gap-2 mb-2">
                        <Input
                          placeholder="Name"
                          value={member.name}
                          onChange={(e) => updateTeamMember(index, 'name', e.target.value)}
                          className="flex-1"
                        />
                        <Input
                          placeholder="Role"
                          value={member.role}
                          onChange={(e) => updateTeamMember(index, 'role', e.target.value)}
                          className="flex-1"
                        />
                        <Button onClick={() => removeTeamMember(index)} variant="ghost" size="sm">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <div className="mb-2">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              handleTeamPhotoUpload(index, e.target.files[0]);
                            }
                          }}
                          className="text-sm"
                        />
                      </div>
                      {member.photo && (
                        <img src={member.photo} alt="Preview" className="w-20 h-20 rounded-full object-cover mb-2" />
                      )}
                      <Textarea
                        placeholder="Bio"
                        value={member.bio}
                        onChange={(e) => updateTeamMember(index, 'bio', e.target.value)}
                        rows={2}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Newsletter */}
              <div>
                <label className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    checked={businessInfo.newsletter.enabled}
                    onChange={(e) => setBusinessInfo({
                      ...businessInfo,
                      newsletter: { ...businessInfo.newsletter, enabled: e.target.checked }
                    })}
                  />
                  <span className="font-medium">Enable Newsletter Signup</span>
                </label>
                {businessInfo.newsletter.enabled && (
                  <Textarea
                    placeholder="Newsletter description (e.g., Subscribe for updates and special offers)"
                    value={businessInfo.newsletter.description}
                    onChange={(e) => setBusinessInfo({
                      ...businessInfo,
                      newsletter: { ...businessInfo.newsletter, description: e.target.value }
                    })}
                    rows={2}
                  />
                )}
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button
              onClick={goToPreview}
              className="flex-1 bg-blue-600 hover:bg-blue-700 py-6 text-lg"
            >
              Preview Website
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // STEP 2: PREVIEW & EDIT
  if (step === 'preview') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Top Bar */}
        <div className="bg-white border-b sticky top-0 z-50 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => setStep('collect')} variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Edit Info
              </Button>
              <div>
                <h2 className="font-bold">Preview Mode</h2>
                <p className="text-xs text-gray-600">Click any section to edit</p>
              </div>
            </div>
            
            {/* Progress */}
            <div className="hidden md:flex items-center gap-2">
              <div className="w-20 h-2 bg-blue-600 rounded-full"></div>
              <div className="w-20 h-2 bg-blue-600 rounded-full"></div>
              <div className="w-20 h-2 bg-blue-600 rounded-full"></div>
              <div className="w-20 h-2 bg-gray-200 rounded-full"></div>
            </div>

            <Button
              onClick={publishWebsite}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : website ? (
                <><Save className="w-4 h-4 mr-2" /> Save & Republish</>
              ) : (
                <><Check className="w-4 h-4 mr-2" /> Publish Website</>
              )}
            </Button>
          </div>
        </div>

        {/* Website Preview */}
        <div className="max-w-7xl mx-auto p-4">
          {/* Hero Section - Editable */}
          <div 
            className="relative group cursor-pointer mb-4"
            onClick={() => setEditingSection('hero')}
          >
            <div 
              className="text-white p-20 rounded-lg relative overflow-hidden bg-gradient-to-br from-blue-600 to-blue-800"
              style={{
                backgroundImage: selectedTemplate?.hero?.backgroundImage 
                  ? `url(${selectedTemplate.hero.backgroundImage})` 
                  : `url('https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1920&q=80')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundBlendMode: 'overlay'
              }}
            >
              <div 
                className="absolute inset-0" 
                style={{ 
                  background: selectedTemplate?.hero?.overlay || 'rgba(37, 99, 235, 0.75)' 
                }}
              ></div>
              <div className="text-center max-w-4xl mx-auto relative z-10">
                {userProfile?.profilePicUrl && (
                  <img
                    src={userProfile.profilePicUrl}
                    alt="Profile"
                    className="w-32 h-32 rounded-full mx-auto mb-6 border-4 border-white object-cover shadow-xl"
                  />
                )}
                <h1 className="text-5xl font-bold mb-4">{template.heroHeading}</h1>
                <p className="text-2xl opacity-90 mb-4">{template.heroSubheading}</p>
                {businessInfo.tagline && (
                  <p className="text-lg opacity-80">{businessInfo.tagline}</p>
                )}
              </div>
            </div>
            <div className="absolute top-4 right-4 hidden group-hover:flex gap-2 z-20">
              <div className="bg-white px-3 py-1 rounded-full text-sm font-medium text-gray-700 shadow">
                <Edit className="w-4 h-4 inline mr-1" />
                Click to edit
              </div>
            </div>
          </div>

          {/* About Section */}
          {businessInfo.about && (
            <div 
              className="bg-white p-12 rounded-lg mb-4 relative group cursor-pointer"
              onClick={() => setEditingSection('about')}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                {template.aboutHeading}
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed max-w-4xl mx-auto whitespace-pre-wrap">
                {businessInfo.about}
              </p>
              <div className="absolute top-4 right-4 hidden group-hover:block">
                <div className="bg-blue-50 px-3 py-1 rounded-full text-sm font-medium text-blue-600">
                  <Edit className="w-4 h-4 inline mr-1" />
                  Edit
                </div>
              </div>
            </div>
          )}

          {/* Services Section */}
          {businessInfo.services.length > 0 && (
            <div className="bg-gray-50 p-12 rounded-lg mb-4">
              <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
                {template.servicesHeading}
              </h2>
              <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {businessInfo.services.map((service, index) => (
                  <div 
                    key={index} 
                    className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-shadow"
                  >
                    <div className="text-4xl mb-4">
                      {service.icon || selectedTemplate?.sections?.services?.defaultIcons[index] || '⭐'}
                    </div>
                    <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Portfolio Section */}
          {businessInfo.portfolio.length > 0 && (
            <div className="bg-white p-12 rounded-lg mb-4">
              <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
                {template.portfolioHeading}
              </h2>
              <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {businessInfo.portfolio.map((item, index) => (
                  <div key={index} className="rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow group">
                    <div className="w-full h-48 bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center overflow-hidden">
                      <img 
                        src={item.image || selectedTemplate?.sections?.portfolio?.defaultImages?.[index] || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80'} 
                        alt={item.title || 'Portfolio item'} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-slate-400"><svg class="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg></div>';
                        }}
                      />
                    </div>
                    <div className="p-6 bg-white">
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contact Section */}
          <div className="bg-gray-900 text-white p-12 rounded-lg">
            <h2 className="text-3xl font-bold mb-8 text-center">{template.contactHeading}</h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="space-y-4">
                {userProfile?.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-blue-400" />
                    <a href={`mailto:${userProfile.email}`} className="text-blue-400">
                      {userProfile.email}
                    </a>
                  </div>
                )}
                {userProfile?.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-blue-400" />
                    <a href={`tel:${userProfile.phone}`} className="text-blue-400">
                      {userProfile.phone}
                    </a>
                  </div>
                )}
                {userProfile?.address && (
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-blue-400" />
                    <p>{userProfile.address}</p>
                  </div>
                )}
              </div>
              {businessInfo.hours && (
                <div>
                  <h3 className="font-bold mb-3">Business Hours</h3>
                  <p className="whitespace-pre-wrap text-gray-300">{businessInfo.hours}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Edit Modal (Simple for now) */}
        {editingSection && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Edit {editingSection}</h3>
                <Button onClick={() => setEditingSection(null)} variant="ghost">×</Button>
              </div>
              
              {editingSection === 'hero' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Main Heading</label>
                    <Input
                      value={template.heroHeading}
                      onChange={(e) => setTemplate({ ...template, heroHeading: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Subheading</label>
                    <Input
                      value={template.heroSubheading}
                      onChange={(e) => setTemplate({ ...template, heroSubheading: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Background Color</label>
                    <input
                      type="color"
                      value={template.primaryColor}
                      onChange={(e) => setTemplate({ ...template, primaryColor: e.target.value })}
                      className="w-full h-10 rounded"
                    />
                  </div>
                </div>
              )}

              {editingSection === 'about' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Section Heading</label>
                    <Input
                      value={template.aboutHeading}
                      onChange={(e) => setTemplate({ ...template, aboutHeading: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Content</label>
                    <Textarea
                      value={businessInfo.about}
                      onChange={(e) => setBusinessInfo({ ...businessInfo, about: e.target.value })}
                      rows={6}
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3 mt-6">
                <Button onClick={() => setEditingSection(null)} variant="outline" className="flex-1">
                  Cancel
                </Button>
                <Button onClick={() => setEditingSection(null)} className="flex-1">
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // STEP 3: PUBLISHED
  if (step === 'published') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Website Published! 🎉</h1>
            <p className="text-xl text-gray-600">Your website is live and ready to share</p>
          </div>

          <Card className="mb-6">
            <CardContent className="p-8">
              <div className="space-y-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <h3 className="font-bold text-lg mb-2">Your Website is Live!</h3>
                  <p className="text-gray-600 mb-1">
                    Published on {new Date(website?.createdAt).toLocaleDateString()}
                  </p>
                  {website?.updatedAt && website.updatedAt !== website.createdAt && (
                    <p className="text-gray-600 mb-4">
                      Last updated on {new Date(website?.updatedAt).toLocaleDateString()} at {new Date(website?.updatedAt).toLocaleTimeString()}
                    </p>
                  )}
                  {(!website?.updatedAt || website.updatedAt === website.createdAt) && (
                    <p className="text-gray-600 mb-4"></p>
                  )}
                  <div className="flex gap-3">
                    <Button
                      onClick={() => window.open(`/auto-site/${website?.id}`, '_blank')}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View Website
                    </Button>
                    <Button
                      onClick={() => {
                        setStep('collect');
                        toast.info('Edit your website content and republish');
                      }}
                      variant="outline"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Full Edit
                    </Button>
                  </div>
                </div>

                <div>
                  <h3 className="font-bold mb-3">What's Included:</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>✓ Hero section with your business info</li>
                    {businessInfo.about && <li>✓ About section</li>}
                    {businessInfo.services?.length > 0 && <li>✓ Services section ({businessInfo.services.length} services)</li>}
                    {businessInfo.portfolio?.length > 0 && <li>✓ Portfolio section ({businessInfo.portfolio.length} projects)</li>}
                    {businessInfo.pricing?.length > 0 && <li>✓ Pricing section ({businessInfo.pricing.length} plans)</li>}
                    {businessInfo.testimonials?.length > 0 && <li>✓ Testimonials section ({businessInfo.testimonials.length} testimonials)</li>}
                    {businessInfo.faq?.length > 0 && <li>✓ FAQ section ({businessInfo.faq.length} questions)</li>}
                    {businessInfo.team?.length > 0 && <li>✓ Team section ({businessInfo.team.length} members)</li>}
                    {businessInfo.newsletter?.enabled && <li>✓ Newsletter signup section</li>}
                    <li>✓ Contact section with business hours</li>
                  </ul>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium mb-2">💡 Pro Tip</h4>
                  <p className="text-sm text-gray-700">
                    Share your website URL with customers, add it to your email signature, and promote it on social media!
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Custom Domain Manager */}
          <div className="mb-6">
            <CustomDomainManager
              siteId={website?.id}
              currentDomain={website?.customDomain}
              onDomainUpdated={(domain, verified) => {
                if (website) {
                  setWebsite({
                    ...website,
                    customDomain: domain,
                    domainVerified: verified
                  });
                }
              }}
            />
          </div>

          <div className="flex gap-3">
            <Button
              onClick={() => {
                setWebsite(null);
                setSelectedTemplate(null);
                setBusinessInfo({
                  services: [],
                  about: '',
                  portfolio: [],
                  hours: '',
                  tagline: ''
                });
                setStep('template');
              }}
              variant="outline"
              className="flex-1"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Create New Website
            </Button>
            <Button
              onClick={() => navigate('/')}
              className="flex-1"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default QuickWebsiteBuilder;
